import { faUser, faEnvelope, faPhone, faBuilding, faTag, faCheckCircle, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FeatherIcon from "feather-icons-react";
import Button from "components/base/Button";
import Badge from "components/base/Badge";
import { Form, Modal, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import { useForm, SubmitHandler } from "react-hook-form";
import axiosInstance from "helpers/api";
import { formatErrorForReact } from "helpers/utils";
import { useEffect, useState } from "react";
import './AddLeadModal.css'

interface AddLeadModalProps {
  show: boolean;
  handleClose: () => void;
  setLeads: any;
  leads: any;
}

type Inputs = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  lead_source: string;
};

const AddLeadModal = ({ show, handleClose, setLeads }: AddLeadModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Inputs>();
  
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [leadType, setLeadType] = useState(null);
  const [leadStatus, setLeadStatus] = useState<any>(null);
  const [leadTypes, setLeadTypes] = useState([]);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState([]);
  const [typeError, setTypeError] = useState(false);
  const [statusError, setStatusError] = useState(false);

  // Watch form values for progress calculation
  const watchedFields = watch();
  
  // Calculate form completion percentage
  const calculateProgress = () => {
    let filledFields = 0;
    
    // Required fields count double
    if (watchedFields.first_name?.trim()) filledFields += 2;
    if (watchedFields.phone?.trim()) filledFields += 2;
    
    // Optional fields count single
    if (watchedFields.last_name?.trim()) filledFields += 1;
    if (watchedFields.email?.trim()) filledFields += 1;
    if (watchedFields.lead_source?.trim()) filledFields += 1;
    
    const maxScore = 4 + 3 + (leadType ? 2 : 0) + (leadStatus ? 2 : 0); // 4 required + 3 optional + 2 leadType + 2 leadStatus
    const currentScore = filledFields + (leadType ? 2 : 0) + (leadStatus ? 2 : 0);
    
    return Math.round((currentScore / maxScore) * 100);
  };

  const fetchLeadTypes = async () => {
    try {
      const response = await axiosInstance.get("/leads/types");
      setLeadTypes(response.data.data.leadTypes);
    } catch (error: any) {
      console.error(error?.response?.data?.message);
    }
  };

  const matchStatus = async (id: any) => {
    setLeadType(id);
    setTypeError(false);

    try {
      const response = await axiosInstance.get(`/leads/status/${id}`);
      setSelectedLeadStatus(response.data.data.leadTypes);
    } catch (error: any) {
      console.error(error.response.data.message);
    }
  };

  const handleSelectStatus = (e: any) => {
    setLeadStatus(e);
    setStatusError(false);
  };

  const nextStep = () => {
    const requiredFirstStep = watchedFields.first_name?.trim() && watchedFields.phone?.trim();
    if (currentStep === 1 && !requiredFirstStep) {
      toast.error("Please fill in the required fields");
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 2));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  useEffect(() => {
    fetchLeadTypes();
  }, []);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!leadType) {
      setTypeError(true);
      return;
    }
    if (!leadStatus) {
      setStatusError(true);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.post("/leads", {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        lead_source: data.lead_source,
        lead_types_id: leadType,
        lead_type_status_id: leadStatus,
      });

      setLeads(response.data.data.leads);
      toast.success(response.data.message);
      reset();
      setCurrentStep(1);
      setLeadType(null);
      setLeadStatus(null);
      setSelectedLeadStatus([]);
      setLoading(false);
      handleClose();
    } catch (error: any) {
      toast.error(formatErrorForReact(error?.response?.data?.message || error?.response?.data || error));
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    reset();
    setCurrentStep(1);
    setLeadType(null);
    setLeadStatus(null);
    setSelectedLeadStatus([]);
    setTypeError(false);
    setStatusError(false);
    handleClose();
  };

  const progress = calculateProgress();

  return (
    <Modal
      size="lg"
      show={show}
      centered
      backdrop="static"
      onHide={handleModalClose}
      className="modern-modal"
    >
      <div className="modern-modal-content">
        {/* Enhanced Header */}
        <div className="modal-header-modern">
          <div className="header-content">
            <div className="header-icon">
              <FontAwesomeIcon icon={faUserPlus} />
            </div>
            <div className="header-text">
              <h2 className="modal-title">Add New Lead</h2>
              <p className="modal-subtitle">
                Create a new lead entry in your CRM system
              </p>
            </div>
          </div>
          <Button
            variant="link"
            className="close-btn p-0 d-flex align-items-center justify-content-center"
            onClick={handleModalClose}
          >
            <FeatherIcon icon="x" size={20} />
          </Button>
        </div>

        {/* Progress Indicator */}
        <div className="progress-section">
          <div className="progress-info">
            <span className="progress-label">Completion Progress</span>
            <span className="progress-percentage">{progress}%</span>
          </div>
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step Indicator */}
        <div className="step-indicator">
          <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
            <div className="step-circle">
              <FontAwesomeIcon icon={faUser} />
            </div>
            <span className="step-label">Contact Info</span>
          </div>
          <div className="step-connector" />
          <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
            <div className="step-circle">
              <FontAwesomeIcon icon={faTag} />
            </div>
            <span className="step-label">Lead Details</span>
          </div>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="modal-body-modern">
            {currentStep === 1 && (
              <div className="form-step">
                <div className="step-header">
                  <h3 className="step-title">Contact Information</h3>
                  <p className="step-description">
                    Enter the basic contact details for the new lead
                  </p>
                </div>

                <div className="form-grid">
                  <div className="form-group-modern">
                    <label className="form-label-modern">
                      <FontAwesomeIcon icon={faUser} className="label-icon" />
                      First Name
                      <span className="required-indicator">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        className={`form-input-modern ${errors.first_name ? 'error' : ''}`}
                        placeholder="Enter first name"
                        {...register("first_name", {
                          required: "First name is required",
                          maxLength: { value: 100, message: "Maximum 100 characters" },
                        })}
                      />
                      {errors.first_name && (
                        <div className="error-message">
                          <FeatherIcon icon="alert-circle" size={14} />
                          {errors.first_name.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group-modern">
                    <label className="form-label-modern">
                      <FontAwesomeIcon icon={faUser} className="label-icon" />
                      Last Name
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        className="form-input-modern"
                        placeholder="Enter last name"
                        {...register("last_name", { 
                          maxLength: { value: 200, message: "Maximum 200 characters" } 
                        })}
                      />
                    </div>
                  </div>

                  <div className="form-group-modern">
                    <label className="form-label-modern">
                      <FontAwesomeIcon icon={faEnvelope} className="label-icon" />
                      Email Address
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="email"
                        className="form-input-modern"
                        placeholder="Enter email address"
                        {...register("email", { 
                          maxLength: { value: 80, message: "Maximum 80 characters" },
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                          }
                        })}
                      />
                      {errors.email && (
                        <div className="error-message">
                          <FeatherIcon icon="alert-circle" size={14} />
                          {errors.email.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group-modern">
                    <label className="form-label-modern">
                      <FontAwesomeIcon icon={faPhone} className="label-icon" />
                      Phone Number
                      <span className="required-indicator">*</span>
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="tel"
                        className={`form-input-modern ${errors.phone ? 'error' : ''}`}
                        placeholder="Enter phone number"
                        {...register("phone", {
                          required: "Phone number is required",
                          maxLength: { value: 100, message: "Maximum 100 characters" },
                        })}
                      />
                      {errors.phone && (
                        <div className="error-message">
                          <FeatherIcon icon="alert-circle" size={14} />
                          {errors.phone.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group-modern full-width">
                    <label className="form-label-modern">
                      <FontAwesomeIcon icon={faBuilding} className="label-icon" />
                      Lead Source
                    </label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        className="form-input-modern"
                        placeholder="Where did this lead come from? (e.g., Website, Referral, Social Media)"
                        {...register("lead_source", { 
                          maxLength: { value: 80, message: "Maximum 80 characters" } 
                        })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="form-step">
                <div className="step-header">
                  <h3 className="step-title">Lead Classification</h3>
                  <p className="step-description">
                    Set the lead type and initial status for proper categorization
                  </p>
                </div>

                <div className="form-grid classification-grid">
                  <div className="form-group-modern">
                    <label className="form-label-modern">
                      <FontAwesomeIcon icon={faTag} className="label-icon" />
                      Lead Type
                      <span className="required-indicator">*</span>
                    </label>
                    <div className="select-wrapper">
                      <select
                        className={`form-select-modern ${typeError ? 'error' : ''}`}
                        onChange={(e) => matchStatus(e.target.value)}
                        value={leadType || ''}
                      >
                        <option value="" disabled>
                          Choose lead type
                        </option>
                        {leadTypes.map((item: any, i: any) => (
                          <option key={i} value={item.id}>
                            {item.lead_type}
                          </option>
                        ))}
                      </select>
                      {typeError && (
                        <div className="error-message">
                          <FeatherIcon icon="alert-circle" size={14} />
                          Lead Type is required
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-group-modern">
                    <label className="form-label-modern">
                      <FontAwesomeIcon icon={faCheckCircle} className="label-icon" />
                      Initial Status
                      <span className="required-indicator">*</span>
                    </label>
                    <div className="select-wrapper">
                      <select
                        className={`form-select-modern ${statusError ? 'error' : ''}`}
                        onChange={(e) => handleSelectStatus(e.target.value)}
                        value={leadStatus || ''}
                        disabled={!leadType}
                      >
                        <option value="" disabled>
                          {!leadType ? 'Select lead type first' : 'Choose initial status'}
                        </option>
                        {selectedLeadStatus.map((item: any, i: any) => (
                          <option key={i} value={item.id}>
                            {item.status}
                          </option>
                        ))}
                      </select>
                      {statusError && (
                        <div className="error-message">
                          <FeatherIcon icon="alert-circle" size={14} />
                          Status is required
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {leadType && leadStatus && (
                  <div className="preview-card">
                    <div className="preview-header">
                      <FeatherIcon icon="eye" size={16} />
                      <span>Lead Preview</span>
                    </div>
                    <div className="preview-content">
                      <div className="preview-item">
                                                 <strong>{watchedFields.first_name} {watchedFields.last_name}</strong>
                         <Badge variant="phoenix" bg="primary" className="ms-2">
                           {(() => {
                             const foundType = leadTypes.find((type: any) => type.id === leadType) as any;
                             return foundType ? foundType.lead_type : 'Unknown Type';
                           })()}
                         </Badge>
                      </div>
                      <div className="preview-item">
                        <FeatherIcon icon="mail" size={14} className="me-2" />
                        {watchedFields.email || 'No email provided'}
                      </div>
                      <div className="preview-item">
                        <FeatherIcon icon="phone" size={14} className="me-2" />
                        {watchedFields.phone}
                      </div>
                      {watchedFields.lead_source && (
                        <div className="preview-item">
                          <FeatherIcon icon="external-link" size={14} className="me-2" />
                          Source: {watchedFields.lead_source}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer-modern">
            <div className="footer-actions">
              {currentStep === 1 ? (
                <>
                  <Button
                    variant="outline-secondary"
                    className="footer-btn secondary"
                    onClick={handleModalClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    className="footer-btn primary"
                    onClick={nextStep}
                    disabled={!watchedFields.first_name?.trim() || !watchedFields.phone?.trim()}
                  >
                    Continue
                    <FeatherIcon icon="arrow-right" size={16} className="ms-2" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline-secondary"
                    className="footer-btn secondary"
                    onClick={prevStep}
                  >
                    <FeatherIcon icon="arrow-left" size={16} className="me-2" />
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    className="footer-btn primary"
                    type="submit"
                    loading={loading}
                    disabled={loading || !leadType || !leadStatus}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUserPlus} className="me-2" />
                        Create Lead
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </Form>
      </div>

      <style>{`
        .modern-modal .modal-dialog {
          max-width: 800px;
        }

        .modern-modal-content {
          background: #ffffff;
          border-radius: 1rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          border: none;
          overflow: hidden;
        }

        /* Enhanced Header */
        .modal-header-modern {
          padding: 2rem 2rem 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: none;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          width: 3rem;
          height: 3rem;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.25rem;
        }

        .header-text h2 {
          margin: 0;
          font-size: 1.75rem;
          font-weight: 700;
          color: #1e293b;
        }

        .modal-subtitle {
          margin: 0.25rem 0 0 0;
          color: #64748b;
          font-size: 0.875rem;
        }

        .close-btn {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 0.5rem;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: #f1f5f9;
          color: #ef4444;
          border-color: #fecaca;
        }

        /* Progress Section */
        .progress-section {
          padding: 1rem 2rem;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }

        .progress-info {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .progress-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #475569;
        }

        .progress-percentage {
          font-size: 0.875rem;
          font-weight: 600;
          color: #3b82f6;
        }

        .progress-bar-container {
          width: 100%;
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          overflow: hidden;
        }

        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #3b82f6, #1d4ed8);
          border-radius: 3px;
          transition: width 0.3s ease;
        }

        /* Step Indicator */
        .step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem 2rem;
          background: white;
          border-bottom: 1px solid #e2e8f0;
        }

        .step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
        }

        .step.active .step-circle {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          transform: scale(1.1);
        }

        .step.active .step-label {
          color: #3b82f6;
          font-weight: 600;
        }

        .step-circle {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 50%;
          background: #e2e8f0;
          color: #64748b;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .step-label {
          font-size: 0.875rem;
          color: #64748b;
          transition: all 0.3s ease;
        }

        .step-connector {
          width: 4rem;
          height: 2px;
          background: #e2e8f0;
          margin: 0 1rem;
        }

        /* Form Styling */
        .modal-body-modern {
          padding: 2rem;
          min-height: 400px;
        }

        .form-step {
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .step-header {
          margin-bottom: 2rem;
          text-align: center;
        }

        .step-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .step-description {
          color: #64748b;
          margin: 0;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        .form-grid.classification-grid {
          grid-template-columns: 1fr;
          max-width: 500px;
          margin: 0 auto;
        }

        .form-group-modern.full-width {
          grid-column: 1 / -1;
        }

        .form-group-modern {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label-modern {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .label-icon {
          color: #6b7280;
          font-size: 0.75rem;
        }

        .required-indicator {
          color: #ef4444;
          font-weight: 700;
        }

        .input-wrapper,
        .select-wrapper {
          position: relative;
        }

        .form-input-modern,
        .form-select-modern {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          background: white;
          transition: all 0.2s ease;
        }

        .form-input-modern:focus,
        .form-select-modern:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
        }

        .form-input-modern.error,
        .form-select-modern.error {
          border-color: #ef4444;
        }

        .form-select-modern:disabled {
          background: #f9fafb;
          color: #9ca3af;
          cursor: not-allowed;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #ef4444;
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }

        /* Preview Card */
        .preview-card {
          margin-top: 2rem;
          padding: 1.5rem;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 0.75rem;
        }

        .preview-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
          font-weight: 600;
          color: #374151;
        }

        .preview-content {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .preview-item {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
          color: #4b5563;
        }

        /* Footer */
        .modal-footer-modern {
          padding: 1.5rem 2rem;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
        }

        .footer-actions {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
        }

        .footer-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 0.75rem;
          font-weight: 600;
          transition: all 0.2s ease;
          border: none;
          display: flex;
          align-items: center;
        }

        .footer-btn.secondary {
          background: white;
          color: #6b7280;
          border: 2px solid #e5e7eb;
        }

        .footer-btn.secondary:hover {
          background: #f9fafb;
          border-color: #d1d5db;
        }

        .footer-btn.primary {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          box-shadow: 0 4px 14px rgba(59, 130, 246, 0.25);
        }

        .footer-btn.primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
        }

        .footer-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
          
          .modal-header-modern {
            padding: 1.5rem;
          }
          
          .modal-body-modern {
            padding: 1.5rem;
          }
          
          .footer-actions {
            flex-direction: column;
          }
        }
      `}</style>
    </Modal>
  );
};

export default AddLeadModal;
